// @ts-check

import { graphql, Link } from "gatsby";
import React from "react";

// import CardArticleWithLayout from "../components/CardArticleWithLayout";
import { Layout } from "../components/Layout";
import SocialShare from "../components/SocialShare";
import Author from "./components/Author";
import Header from "../components/Header";
import DateLabel from "../components/DateLabel";
import ImgContainer from "../components/ImgContainer";
import BottomBorder from "../components/BottomBorder";

import ArticleCard from "./components/ArticleCard";
import ArticleContainer from "./components/ArticleContainer";
import useAuthorContacts from "./hooks/useAuthorContacts";
import TitleAndMeta from "../components/TitleAndMeta";

const articleAuthor = {
  twitter: "",
  fb: "",
};

const more = "المزيد في المدونة";

const getSlug = (url) => (new URL(url).pathname.match(/[^\/]+/g) || [])[0];

const stringifyArr = (arr, key) => {
  let str = null;

  if (arr) {
    str = "";

    const { length } = arr;

    arr.forEach((elm, i) => {
      if (i !== 0 && length > 0) {
        str += "- ";
      }

      str += `${elm[key]}`;

      if (length > 1 && i !== length - 1) {
        str += " ";
      }
    });

    str = str.trim();
  }

  return str;
};

function BlogRecommendation({ data, isJustifyEnd }) {
  const { slug, title, createdAt, publish_at: date = createdAt, cover } = data;

  return (
    <div
      className={`flex w-full py-4 md:w-1/2 ${
        isJustifyEnd ? "md:justify-end" : "justify-start"
      }`}
    >
      <ImgContainer
        type="recommended"
        slug={slug}
        image={cover}
        alt={cover.alterativeText}
      />
      <div className="pr-4">
        <Link to={slug}>
          <Header isHover type="linkedPreviewSmall" title={title} />
        </Link>
        {date && <DateLabel hasFullOpts={false} date={date} />}
      </div>
    </div>
  );
}

function Blog({ data }) {
  const { currentBlog, nextBlog, prevBlog } = data;

  const {
    slug,
    title,
    createdAt,
    publish_at: date = createdAt,
    content: {
      data: { content: html },
    },
    cover,
  } = currentBlog;

  const contacts = useAuthorContacts();

  const twitter = contacts.find((_) => _.name === "TWITTER");

  if (twitter) {
    articleAuthor.twitter = getSlug(twitter.link);
  }

  return (
    <Layout isCenter>
      <div className="flex flex-wrap flex-col">
        <ArticleCard
          hasShadow
          hasBorder
          hasRelativePosition
          data={{
            html,
            title,
            slug,
            date,
            cover,
            alt: cover.alterativeText,
          }}
        >
          <SocialShare
            title={title}
            slug={slug}
            articleAuthorTwitterHandle={articleAuthor.twitter}
          />
          <Author isPrimary={false} />
        </ArticleCard>
        <ArticleContainer
          isPreview={false}
          hasShadow={false}
          hasRelativePosition={false}
          isFlexCol
          Component="div"
        >
          <BottomBorder className="w-11/12 md:w-full my-5 md:my-10">
            <Header type="linkedPreviewSmall" isHover={false} title={more} />
          </BottomBorder>
          <div className="flex flex-wrap">
            {prevBlog && (
              <BlogRecommendation data={prevBlog} isJustifyEnd={false} />
            )}
            {nextBlog && (
              <BlogRecommendation data={nextBlog} isJustifyEnd={prevBlog} />
            )}
          </div>
        </ArticleContainer>
      </div>
    </Layout>
  );
}

export const pageQuery = graphql`
  query TemplateBlog($slug: String!, $slugNext: String, $slugPrev: String) {
    currentBlog: strapiArticle(slug: { eq: $slug }) {
      ...StrapiArticleMeta
      ...StrapiArticleCover
      content {
        data {
          content
        }
      }
      description
      tags_and_keywords {
        tag
      }
    }

    nextBlog: strapiArticle(slug: { eq: $slugNext }) {
      ...StrapiBlogBasic
    }

    prevBlog: strapiArticle(slug: { eq: $slugPrev }) {
      ...StrapiBlogBasic
    }
  }
`;

export const query = graphql`
  fragment StrapiBlogBasic on STRAPI_ARTICLE {
    ...StrapiArticleMeta
    ...StrapiArticleCover
  }

  fragment StrapiArticleMeta on STRAPI_ARTICLE {
    slug
    title
    createdAt
    updatedAt
    publish_at
    category {
      category
    }
  }

  fragment StrapiArticleCover on STRAPI_ARTICLE {
    cover {
      alternativeText
      url
      localFile {
        childImageSharp {
          gatsbyImageData(
            width: 700
            height: 400
            quality: 100
            placeholder: BLURRED
            formats: [AUTO, WEBP, AVIF]
          )
        }
      }
    }
  }
`;

export default Blog;

export function Head({ data }) {
  const { currentBlog } = data;

  const contacts = useAuthorContacts();

  const fb = contacts.find((_) => _.name === "FACEBOOK");
  const twitter = contacts.find((_) => _.name === "TWITTER");

  if (fb) {
    articleAuthor.fb = getSlug(fb.link);
  }

  if (twitter) {
    articleAuthor.twitter = getSlug(twitter.link);
  }

  const {
    slug,
    title,
    createdAt,
    updatedAt,
    publish_at: date = createdAt,
    category: { category },
    tags_and_keywords: tags,
    cover,
    description,
  } = currentBlog;

  return (
    <TitleAndMeta
      description={description}
      type="article"
      title={title}
      updatedAt={updatedAt}
      createdAt={date}
      ogImage={cover.url}
      ogImageAlt={cover.alterativeText}
      slug={slug}
      articleAuthor={articleAuthor}
      tags={stringifyArr(tags, "tag")}
      categories={category}
    />
  );
}
